import React, { useEffect, useState, useMemo, useCallback } from "react";
import './FrontPage.css';
import Card from "./card/Card";
import Carousel from "./carousel/Carousel";
import { nftService } from "../../services";
import type { NFTMetadata } from "../../services";
import { NFT } from "../../services/models/NFT";
import { ColorRing } from "react-loader-spinner";


const FrontPage = () => {
  const [loading, setLoading] = useState(true);
  const [nfts, setNFTs] = useState<NFT[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [carouselKey, setCarouselKey] = useState(0);
  
  // Memoized conversion function to prevent unnecessary re-renders
  const convertToNFTFormat = useCallback((nftMetadata: NFTMetadata[]): NFT[] => {
    return nftMetadata.map((nft) => ({
      identifier: nft.identifier,
      name: nft.name,
      collection: nft.collection,
      contract: nft.contract,
      description: nft.description,
      image_url: nft.display_image_url || nft.image_url
    }));
  }, []);

  // Memoized shuffle function to prevent recreation on every render
  const shuffleArray = useCallback((array: NFT[]): NFT[] => {
    const shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
  }, []);

  // Reset carousel key when component mounts
  useEffect(() => {
    setCarouselKey(prev => prev + 1);
  }, []);

  const fetchNFTs = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const nftMetadataList = await nftService.getNFTs(1000, forceRefresh);
      
      const nftList = convertToNFTFormat(nftMetadataList);
      
      if (nftList.length === 0) {
        setError('No NFTs found. Please try again.');
        setLoading(false);
        return;
      }
      
      // Pick 30 random NFTs for better carousel performance
      const randomizedNFTs = shuffleArray(nftList).slice(0, 30);
      
      setNFTs(randomizedNFTs);
      setLoading(false);
      setCarouselKey(prev => prev + 1); // Force carousel re-mount
    } catch (error) {
      console.error('Error fetching NFTs for carousel:', error);
      setError('Failed to load NFTs. Please try again.');
      setLoading(false);
    }
  }, [convertToNFTFormat, shuffleArray]);

  useEffect(() => {
    fetchNFTs(false);
  }, [fetchNFTs]);

  // Memoized carousel to prevent unnecessary re-renders
  const memoizedCarousel = useMemo(() => {
    if (loading) {
      return (
        <ColorRing
          visible={true}
          height="160"
          width="160"
          ariaLabel="color-ring-loading"
          wrapperStyle={{}}
          wrapperClass="color-ring-wrapper"
          colors={['#e15b64', '#f47e60', '#f8b26a', '#abbd81', '#849b87']}
        />
      );
    }
    
    if (error) {
      return (
        <div className="error-message">
          <p>{error}</p>
          <button 
            onClick={() => fetchNFTs(true)} 
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#4a90e2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    
    return <Carousel key={carouselKey} items={nfts} />;
  }, [loading, error, nfts, carouselKey, fetchNFTs]);

  return (
    <div className="front-page-container">
      <div className="carousel-container">
        {memoizedCarousel}
      </div>
      <div className="front-page-content">
        <Card 
          title="ColorFuel Simulation" 
          description="Experience the ColorFuel 3D Gallery" 
          image="/assets/simulation_screenshot.png" 
          linkTo="/3dgallery"
          className="simulation-card"
        />
        <Card 
          title="ColorFuel Gallery" 
          description="Visit ColorFuel NFT Gallery" 
          image="https://cdn.pixabay.com/photo/2024/06/24/06/42/art-appraiser-8849270_1280.jpg" 
          linkTo="/discover/ethereum"
        />
        <Card 
          title="Create NFT" 
          description="Create your own NFTs" 
          image="https://cdn.pixabay.com/photo/2022/02/10/09/39/nft-7004985_1280.jpg"
          linkTo="/create"
        />
        <Card 
          title="About ColorFuel" 
          description="What is ColorFuel?"
          image="https://cdn.pixabay.com/photo/2020/03/22/17/47/scrabble-4957948_1280.jpg" 
          linkTo="/about"
        />
      </div>
  </div>
  );
}

export default FrontPage;